import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { StatusBadge } from '../components/StatusBadge'
import { deviceService } from '../services'
import type { Device } from '../types'
import { formatDateTime, formatDistanceToNow } from '../utils/date'
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  Edit3,
  Globe,
  Hash,
  Loader2,
  Monitor,
  RefreshCw,
  Save,
  Shield,
  Tag,
  X,
  Zap,
} from 'lucide-react'

export function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [device, setDevice] = useState<Device | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [saveError, setSaveError] = useState('')

  const fetchDevice = async () => {
    if (!id) return
    try {
      setLoading(true)
      setError('')
      const data = await deviceService.getDevice(id)
      setDevice(data)
      setName(data.name || '')
      setDescription(data.description || '')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load device')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDevice()
  }, [id])

  const handleSave = async () => {
    if (!id) return
    try {
      setSaving(true)
      setSaveError('')
      const updated = await deviceService.updateDevice(id, { name, description })
      setDevice(updated)
      setIsEditing(false)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save device')
    } finally {
      setSaving(false)
    }
  }

  const detailItems = device
    ? [
        { icon: Hash, label: 'ZT Address', value: device.id },
        { icon: Monitor, label: 'Name', value: device.name || 'Unnamed' },
        { icon: Globe, label: 'ZT IP', value: device.zt_ip || '—' },
        { icon: Globe, label: 'Physical IP', value: device.physical_ip || '—' },
        { icon: Shield, label: 'Role', value: device.role || '—' },
        { icon: Tag, label: 'Version', value: device.version || '—' },
        { icon: Zap, label: 'Latency', value: device.latency !== undefined ? `${device.latency} ms` : '—' },
        { icon: Calendar, label: 'Created', value: device.created_at ? formatDateTime(device.created_at) : '—' },
        { icon: Clock, label: 'Last Seen', value: device.last_seen ? `${formatDateTime(device.last_seen)} (${formatDistanceToNow(device.last_seen)})` : '—' },
      ]
    : []

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard"
            className="btn btn-secondary text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Device Details</h1>
            <p className="text-text-muted text-sm">View and manage device information</p>
          </div>
        </div>

        {loading ? (
          <div className="card p-12 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="card p-8 text-center">
            <div className="p-4 rounded-md bg-danger/10 text-danger text-sm inline-flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
            <button
              type="button"
              onClick={fetchDevice}
              className="btn btn-primary mt-4"
            >
              Retry
            </button>
          </div>
        ) : device ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="card p-6">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-primary-50 flex items-center justify-center">
                      <Monitor className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">
                        {device.name || 'Unnamed Device'}
                      </h2>
                      <p className="font-mono text-sm text-text-muted">{device.id}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <StatusBadge online={device.online} pulse={device.online} />
                    {device.authorized && (
                      <span className="badge badge-success">
                        <CheckCircle2 className="w-3 h-3" />
                        Authorized
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {detailItems.map(item => (
                    <div key={item.label} className="p-4 rounded-lg bg-surface-elevated">
                      <div className="flex items-center gap-2 text-text-muted text-sm mb-1">
                        <item.icon className="w-4 h-4" />
                        {item.label}
                      </div>
                      <div className="font-mono text-sm break-all">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="card p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Metadata</h3>
                  <button
                    type="button"
                    onClick={() => {
                      if (isEditing) {
                        setName(device.name || '')
                        setDescription(device.description || '')
                      }
                      setIsEditing(!isEditing)
                      setSaveError('')
                    }}
                    className="btn btn-secondary text-sm"
                  >
                    {isEditing ? (
                      <>
                        <X className="w-4 h-4" /> Cancel
                      </>
                    ) : (
                      <>
                        <Edit3 className="w-4 h-4" /> Edit
                      </>
                    )}
                  </button>
                </div>

                {saveError && (
                  <div className="mb-4 p-3 rounded-md bg-danger/10 text-danger text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {saveError}
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      disabled={!isEditing}
                      className="input disabled:opacity-60"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Description</label>
                    <textarea
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      disabled={!isEditing}
                      rows={4}
                      className="input resize-none disabled:opacity-60"
                    />
                  </div>

                  {isEditing && (
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                      className="btn btn-primary disabled:opacity-50"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" /> Save Changes
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="card p-6">
                <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={fetchDevice}
                    className="btn btn-secondary w-full"
                  >
                    <RefreshCw className="w-4 h-4" /> Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Layout>
  )
}

